import { lazy, Suspense, useRef, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from "react-router";
import { SessionProvider, useSession } from "../lib/session";
import { I18nProvider } from "../lib/i18n";
import { ApiError, peekUnauthReason } from "../lib/api";
import { supportReturnUrl } from "../lib/support";
import { PlatformProvider, usePlatform } from "../features/platform/session";
import { PlatformShell } from "../features/platform/PlatformShell";
import { PlatformLoginPage, PlatformOverviewPage, platformRoutes } from "../features/platform/routes";
import { SupportEndedPage } from "./SupportEndedPage";
import { Skeleton } from "../ui/States";
import { BrandLoader } from "../ui/Brand";
import { AppShell } from "./AppShell";
import { LoginPage } from "../features/auth/LoginPage";
import { SignupPage } from "../features/auth/SignupPage";
import { InvitePage } from "../features/auth/InvitePage";
import { ResetPage } from "../features/auth/ResetPage";
import { safeNext } from "../features/auth/next";
import { HomePage } from "../features/home/HomePage";
import { NotFoundPage } from "./NotFoundPage";
import { routes as featureRoutes } from "./routes";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500 && err.status !== 0) && count < 2,
      refetchOnWindowFocus: true,
      staleTime: 15_000,
    },
  },
});

function RequireAuth() {
  const { session, loading, expired, loggedOut } = useSession();
  const location = useLocation();
  if (loading) return <BrandLoader />;
  if (!session) {
    // Destek oturumu düştüyse giriş ekranı yerine "destek oturumu sona erdi" (panele dönüş).
    if (supportReturnUrl()) return <Navigate to="/destek-bitti" replace />;
    if (peekUnauthReason() === "tenant_suspended") return <Navigate to="/giris?neden=askida" replace />;
    if (loggedOut) return <Navigate to="/giris" replace />;
    return <Navigate to={`/giris?sonra=${encodeURIComponent(location.pathname + location.search)}${expired ? "&neden=sure" : ""}`} replace />;
  }
  return (
    <I18nProvider locale={session.user.locale} timezone={session.tenant.timezone}>
      <AppShell />
    </I18nProvider>
  );
}

/**
 * Oturum açıkken giriş/kayıt/davet sayfaları ana ekrana yönlenir. Oturum bu sayfada açıldıysa (giriş, kayıt,
 * davet kabulü) yönlendirmeyi sayfanın kendisi yapar (kayıt → kurulum sihirbazı, giriş → ?sonra=); burada da
 * "/"ya yönlendirmek sayfanın başlattığı gezinmeyi keser ve kullanıcı hedef yerine ana ekrana düşer.
 */
function PublicOnly({ children }: { children: ReactNode }) {
  const { session, loading } = useSession();
  const location = useLocation();
  const openedSignedOut = useRef<boolean | null>(null);
  if (loading) return <BrandLoader />;
  openedSignedOut.current ??= !session;
  // Sayfaya oturum açıkken gelindiyse ?sonra= ile istenen sayfaya (yoksa ana ekrana) gidilir.
  if (session && !openedSignedOut.current) return <Navigate to={safeNext(location.search)} replace />;
  return <I18nProvider locale="tr" timezone="Europe/Istanbul">{children}</I18nProvider>;
}

/** 21ik Platform paneli: firma kabuğundan ayrı ağaç, ayrı oturum (PlatformProvider), tek dil (tr). */
function PlatformRoot() {
  return (
    <I18nProvider locale="tr" timezone="Europe/Istanbul">
      <PlatformProvider>
        <Outlet />
      </PlatformProvider>
    </I18nProvider>
  );
}

function RequirePlatform() {
  const { session, loading } = usePlatform();
  const location = useLocation();
  if (loading) return <BrandLoader />;
  if (!session) return <Navigate to={`/yonetim/giris?sonra=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <PlatformShell />;
}

function PlatformPublicOnly({ children }: { children: ReactNode }) {
  const { session, loading } = usePlatform();
  if (loading) return <BrandLoader />;
  if (session) return <Navigate to="/yonetim" replace />;
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    path: "/yonetim",
    element: <PlatformRoot />,
    children: [
      {
        path: "giris",
        element: (
          <PlatformPublicOnly>
            <Suspense fallback={<BrandLoader />}>
              <PlatformLoginPage />
            </Suspense>
          </PlatformPublicOnly>
        ),
      },
      {
        element: <RequirePlatform />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Skeleton lines={6} />}>
                <PlatformOverviewPage />
              </Suspense>
            ),
          },
          ...platformRoutes.map((r) => ({
            path: r.path,
            element: (
              <Suspense fallback={<Skeleton lines={5} />}>
                <r.Component />
              </Suspense>
            ),
          })),
          { path: "*", element: <Navigate to="/yonetim" replace /> },
        ],
      },
    ],
  },
  {
    path: "/destek-bitti",
    element: (
      <I18nProvider locale="tr" timezone="Europe/Istanbul">
        <SupportEndedPage />
      </I18nProvider>
    ),
  },
  { path: "/giris", element: <PublicOnly><LoginPage /></PublicOnly> },
  { path: "/kurulum", element: <PublicOnly><SignupPage /></PublicOnly> },
  { path: "/davet", element: <PublicOnly><InvitePage /></PublicOnly> },
  { path: "/davet/:token", element: <PublicOnly><InvitePage /></PublicOnly> },
  { path: "/parola-sifirla", element: <PublicOnly><ResetPage /></PublicOnly> },
  { path: "/parola-sifirla/:token", element: <PublicOnly><ResetPage /></PublicOnly> },
  {
    element: <RequireAuth />,
    children: [
      { index: true, element: <HomePage /> },
      ...featureRoutes.map((r) => ({
        path: r.path,
        element: (
          <Suspense fallback={<Skeleton lines={5} />}>
            <r.Component />
          </Suspense>
        ),
      })),
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  );
}

export { lazy, Outlet };
